import { useRef, useEffect, useState, useCallback } from 'react';
import { isEqual } from 'lodash';

/**
 * Hook that returns a ref indicating whether the component is still mounted.
 * Use this to guard state updates in async callbacks.
 *
 * @example
 * const isMounted = useIsMounted();
 * useEffect(() => {
 *   fetchData().then(data => {
 *     if (isMounted()) setState(data);
 *   });
 * }, []);
 */
export const useIsMounted = () => {
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => mountedRef.current, []);
};

/**
 * A drop-in replacement for useState that ignores state updates after unmount.
 * Prevents "Can't perform a React state update on an unmounted component" warnings.
 *
 * @example
 * const [data, setData] = useSafeState(null);
 * // setData will be a no-op if called after component unmounts
 */
export const useSafeState = (initialValue) => {
  const [state, setState] = useState(initialValue);
  const isMounted = useIsMounted();

  const setSafeState = useCallback(
    (value) => {
      if (isMounted()) {
        setState(value);
      }
    },
    [isMounted]
  );

  return [state, setSafeState];
};

/**
 * A custom IntersectionObserver hook that properly disconnects on unmount.
 * Replaces the react-hooks-visible library which never cleaned up its observer.
 *
 * @param {Function} fn - Visibility ratio callback function (ratio => boolean), or falsy for default (ratio > 0)
 * @returns {[React.RefObject, boolean]} - Tuple of [ref, isVisible]
 */
export const useSafeVisible = (fn) => {
  const targetRef = useRef(null);
  const [isVisible, setIsVisible] = useSafeState(false);

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const ratio = entry.intersectionRatio;
          const result = typeof fn === 'function' ? fn(ratio) : ratio > 0;
          setIsVisible(result);
        });
      },
      { threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1.0] }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [targetRef, isVisible];
};

export const useHasWidgetChanged = (newValue) => {
  const previousValue = usePrevious(newValue);
  return !isEqual(previousValue, newValue);
};

const usePrevious = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};


// From: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}
